import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import { AbstractService } from "../../../../services/abstract.service";
import {
    // ... imports de query.util
    andAllWhere,
    andWhere,
    buildQuery,
    CombinedFilter,
    FilterFn,
    noop,
    Ops,
    orderBy,
    OrderVariant,
    orWhere,
    page,
    where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { Observable } from "rxjs";
import { CreditApplicationAttributes } from "@albatrosdeveloper/ave-models-package";
import { ICreateBusinessPartnerCredit, IUpdatePartnerCredit } from "./interfaces/credit.interface";

const apiUrl = env.API_BUSINESS_PARTNER_CREDIT;
const moduleName = "credit_application";

@Injectable({
    providedIn: "root",
})
export class BusinessPartnerCreditService extends AbstractService<
    CreditApplicationAttributes,
    ICreateBusinessPartnerCredit,
    IUpdatePartnerCredit
> {

    /** The last filter used to load the list */
    currentFilter: CombinedFilter<CreditApplicationAttributes>;    

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    /**
     * Anulación del método create para aceptar FormData y subir archivos.
     * @ts-ignore: Se usa para ignorar la incompatibilidad de tipos con la base class.
     * Esto es necesario porque la base class espera ICreateBusinessPartnerCredit (JSON), 
     * pero para subir archivos necesitamos FormData (multipart/form-data).
     */
    // @ts-ignore
    override create(body: FormData): Observable<IRequestResult<CreditApplicationAttributes>> {
        const URL = `${env.API_BUSINESS_PARTNER_CREDIT}api/credit_application`;
        
        // HttpClient detecta FormData y establece automáticamente el Content-Type.
        return this.http.post<IRequestResult<CreditApplicationAttributes>>(URL, body);
    }


    findByFilter(filter: CombinedFilter<CreditApplicationAttributes>, options?: RequestOptions) {
        const API_URL: string =
            andWhere +
            `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
        const headers: Record<string, any> = {};
        // see order-customer-export for more info about this header
        // ctx: stands for context
        if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
        return this.http.get<IPaginationResult<CreditApplicationAttributes>>(API_URL, { headers });
    }

    /**
     * Pagination
     * @param options Pagination options
     * @returns Paginated data
     */
    findAllWithCount(
        options: IPagination<CreditApplicationAttributes>,
        dataToFilter?
    ): Observable<IPaginationResult<CreditApplicationAttributes>> {
        const arrayQueries: FilterFn<CreditApplicationAttributes>[] = [];
        if(dataToFilter.assignedUserId && dataToFilter.assignedUserId.length > 0) {
            arrayQueries.push(andWhere("user.username" as keyof CreditApplicationAttributes, Ops.in(...dataToFilter.assignedUserId)))
        }

        if(dataToFilter.statusERP && dataToFilter.statusERP.length > 0 && !dataToFilter.statusERP.includes(null)) {
            arrayQueries.push(andWhere('statusERP', Ops.in(...dataToFilter.statusERP)))
        }

        if(dataToFilter.status && dataToFilter.status.length > 0 && !dataToFilter.status.includes(null)) {
            arrayQueries.push(andWhere('statusId', Ops.in(...dataToFilter.status)))
        }

        if(dataToFilter.requestCredit && dataToFilter.requestCredit.length > 0){
            arrayQueries.push(andWhere('isExtension', Ops.in(...dataToFilter.requestCredit)))
        }

        if(dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0) {
            arrayQueries.push(andWhere('subRoute.id' as keyof CreditApplicationAttributes, Ops.in(...dataToFilter.subRouteId)))
        } 

        if(dataToFilter.customerId && dataToFilter.customerId.length > 0){
            arrayQueries.push(andWhere("businessPartner.id" as keyof CreditApplicationAttributes, Ops.in(...dataToFilter.customerId)))
        }

        const query = buildQuery<CreditApplicationAttributes>(
            where('_deleted', Ops.eq(false)),
            dataToFilter.startDate && dataToFilter.endDate
            ? andAllWhere("createdAt", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate))
            : noop(),
            ...arrayQueries,
            orderBy(
                options.sort ?? "amountCreditRequested",
                options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        this.currentFilter = query;

        const API_URL: string =
            apiUrl +
            `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IPaginationResult<CreditApplicationAttributes>>(API_URL);
    }
}